import groupedImage from "../assets/softwareconsultinggroup.webp";
import consultantImg from "../assets/consultant.png";
import { motion } from "framer-motion";
import badgesGroupImage from "../assets/grouped-badgesgrouped-badges.svg";
import qualityIcon from "../assets/high-quality.png";
import communicationIcon from "../assets/communication.png";
import flexibilityIcon from "../assets/flexibility.png";
import { Helmet } from "react-helmet-async";

export default function SoftwareConsulting() {
  const features = [
    {
      title: "High-Quality",
      description:
        "Our software is made to be supplied on schedule, with few errors, and in an understandable way.",
      icon: qualityIcon,
      bg: "bg-[#d3f8f2]",
    },
    {
      title: "Communication",
      description:
        "Project's direction is in line with the client's vision; developers keep in regular contact with the client.",
      icon: communicationIcon,
      bg: "bg-[#eee8fd]",
    },
    {
      title: "Flexibility",
      description:
        "Our strategy ensures flexibility and reactivity in every project by keeping up with the latest trends.",
      icon: flexibilityIcon,
      bg: "bg-[#e2fbe9]",
    },
  ];

  const containerVariants = {
    hidden: {},
    show: {
      transition: {
        staggerChildren: 0.2,
      },
    },
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 30 },
    show: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } },
  };

  return (
    <>
      <Helmet>
        <title>
          Expert Software Consulting Services | Accelerate Digital
          Transformation
        </title>
        <meta
          name="description"
          content="Boost your business growth with expert software consulting. Our senior consultants provide strategic guidance, optimized systems, and flexible solutions for smarter decision-making and accelerated digital transformation."
        />
        <meta
          name="keywords"
          content="IT strategy consulting, technology consulting, business technology solutions, software project management, software optimization, IT consulting experts, strategic software solutions
"
        />
      </Helmet>
      <section className="w-full bg-[#FAFAFA] py-20 px-4 sm:px-6">
        <div className="w-full max-w-[1312px] mx-auto flex flex-col lg:flex-row items-center justify-between gap-10">
          {/* LEFT CONTENT */}
          <motion.div
            className="w-full lg:w-[60%]"
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: "easeOut" }}
            viewport={{ once: true }}
          >
            <p className="text-[#119D57] font-semibold text-sm mb-3 uppercase tracking-wider">
              Software Consulting
            </p>
            <h1 className="text-[#1C1C1C] text-[40px] md:text-[48px] leading-[52px] md:leading-[58px] font-extrabold mb-[24px]">
              Accelerate innovation and
              <br className="hidden lg:block" />
              strategy with experts
            </h1>
            <p className="text-[#4D4D4D] text-[16px] md:text-[18px] leading-[28px] md:leading-[30px] max-w-[600px]">
              We provide expert guidance to help businesses choose, design, and
              implement the right technology solutions. It ensures smarter
              decision-making, optimized systems, and accelerated digital
              transformation.
            </p>
          </motion.div>

          {/* RIGHT IMAGE */}
          <motion.div
            className="w-full lg:w-[40%] flex justify-center lg:justify-end"
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, ease: "easeOut", delay: 0.2 }}
            viewport={{ once: true }}
          >
            <img
              src={groupedImage}
              alt="Software Consulting Illustration"
              className="w-[280px] sm:w-[360px] md:w-[420px] lg:w-full max-w-[500px] h-auto object-contain"
            />
          </motion.div>
        </div>
      </section>

      <div className="bg-black text-white px-6 sm:px-8 lg:px-16 py-20 font-poppins">
        <div className="max-w-[1440px] mx-auto">
          {/* Heading Section with Animation */}
          <motion.div
            className="flex flex-col lg:flex-row justify-between items-start mb-12 gap-6"
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2 className="text-[36px] sm:text-[42px] lg:text-[48px] font-bold leading-tight lg:max-w-[700px]">
              What is software <br className="hidden sm:block" /> consulting?
            </h2>
            <p className="text-[16px] text-gray-300 lg:max-w-[500px] leading-relaxed">
              Software consulting is most often used when a company needs an
              outside, expert opinion regarding a business decision.
            </p>
          </motion.div>

          {/* Animated Content Box */}
          <motion.div
            className="flex flex-col md:flex-row items-center border border-gray-700 rounded-[32px] bg-gradient-to-r from-[#1b0d1f] to-[#320947] overflow-hidden shadow-md"
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            viewport={{ once: true }}
          >
            {/* Left - Image */}
            <motion.div
              className="w-full md:w-[40%] flex justify-center items-center p-6 md:p-10"
              initial={{ x: -30, opacity: 0 }}
              whileInView={{ x: 0, opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              viewport={{ once: true }}
            >
              <img
                src={consultantImg}
                alt="Consultant"
                className="w-full h-auto max-w-[320px] object-contain"
              />
            </motion.div>

            {/* Right - Text */}
            <motion.div
              className="w-full md:w-[60%] px-6 md:px-10 py-6 flex flex-col justify-center"
              initial={{ x: 30, opacity: 0 }}
              whileInView={{ x: 0, opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              viewport={{ once: true }}
            >
              <h3 className="text-[24px] sm:text-[28px] font-semibold text-[#e2b8ff] mb-4 leading-snug">
                Role and responsibilities of a{" "}
                <br className="hidden sm:block" />
                software consultant
              </h3>
              <p className="text-base text-gray-300 leading-relaxed">
                Software consultants, often experienced senior developers, lead
                projects across sectors like finance, automotive, and
                government. They coordinate with stakeholders and teams to
                manage timelines, budgets, and deliver strategic solutions.
              </p>
            </motion.div>
          </motion.div>
        </div>
      </div>
      <section className="w-full font-poppins">
        {/* BENEFITS SECTION */}
        <section className="bg-[#F8F8F8] w-full py-20 px-4 sm:px-6 lg:px-8 overflow-hidden">
          <motion.div
            className="max-w-[1240px] mx-auto flex flex-col lg:flex-row items-start justify-between gap-14 lg:gap-20"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, ease: "easeOut" }}
          >
            {/* LEFT SIDE */}
            <div className="w-full lg:w-[50%] text-center lg:text-left">
              <h2 className="text-[28px] sm:text-[36px] md:text-[42px] font-extrabold text-[#0F0F0F] leading-tight mb-6">
                Benefits of our software <br className="hidden sm:block" />
                consulting
              </h2>
              <p className="text-[16px] text-[#4D4D4D] max-w-[520px] mx-auto lg:mx-0">
                Unlock strategic growth and technical excellence with our expert
                software consulting.
              </p>
            </div>

            {/* RIGHT SIDE */}
            <div className="w-full lg:w-[50%]">
              <div className="w-full flex justify-center">
                <h4 className="text-sm font-semibold text-[#222222] mb-3 tracking-wide text-center">
                  FEATURES
                </h4>
              </div>

              <hr className="border-t border-[#E5E5E5] mb-6" />

              <div className="flex justify-center lg:justify-start">
                <img
                  src={badgesGroupImage}
                  alt="Grouped Feature Badges"
                  className="w-[600px] max-w-full object-contain"
                />
              </div>
            </div>
          </motion.div>
        </section>

        {/* KEY FEATURES */}
        <div className="bg-white w-full px-6 md:px-10 lg:px-20 py-16">
          <div className="text-center mb-12 max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-2">
              Key features
            </h2>
            <p className="text-md text-gray-600 max-w-xl mx-auto">
              Discover the game-changing key features that set you apart!
            </p>
          </div>

          <motion.div
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 max-w-7xl mx-auto"
            variants={containerVariants}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.2 }}
          >
            {features.map((feature, index) => (
              <motion.div
                key={index}
                variants={cardVariants}
                className={`rounded-2xl p-8 ${feature.bg} text-center shadow-sm hover:shadow-md transition-all duration-300`}
              >
                <img
                  src={feature.icon}
                  alt={feature.title}
                  className="mx-auto w-14 h-14 mb-6"
                />
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  {feature.title}
                </h3>
                <p className="text-gray-700 text-sm sm:text-base leading-relaxed">
                  {feature.description}
                </p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>
    </>
  );
}
